import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { CloseIcon } from '~/assets/icons';
import { useFormik } from 'formik';
import { cast, handleApiError, notify } from '~/utils/helper';
import { useTranslation } from 'react-i18next';

import { BaseSendEmail } from '~/components/common';
import { IEditNote } from '~/utils/interface/note';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { handleSendMail } from '~/thunks/customer/customerThunk';
import { customerActions } from '~/thunks/customer/customerSlice';
import { handleGetCustomerFiles } from '~/thunks/customer/customerThunk';
import { ISendMail } from '~/utils/interface/mail';

type SendMailDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;

  data?: string;
  id?: string;
};

const initialValues: ISendMail = {
  note: '',
  files: undefined,
  to: '',
  subject: '',
  customerFileIds: [],
  cc: '',
  bcc: '',
  from: '',
};

const SendMailDialog = (props: SendMailDialogProps) => {
  //#region Destructuring Props

  // //#endregion Destructuring Props
  // //#region Declare Hook
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['admin/component', 'admin/common']);
  const { needRefreshData } = useAppSelector((state) => state.customer);
  //#endregion Declare Hook
  useAppSelector((state) => state.customer);
  //#region Selector
  //#endregion Selector
  //#region Declare State

  const [dateSendEmail, setDataSendEmail] = useState<IEditNote>();
  //#endregion Declare State
  //#region Implement Hook
  useEffect(() => {
    form.setFieldValue('customerId', cast(props.id));

    form.setFieldValue('note', cast(dateSendEmail?.note));
    form.setFieldValue('to', cast(dateSendEmail?.to));
    form.setFieldValue('subject', cast(dateSendEmail?.subject));
    form.setFieldValue('cc', cast(dateSendEmail?.cc));
    form.setFieldValue('bcc', cast(dateSendEmail?.bcc));
    form.setFieldValue('from', cast(dateSendEmail?.from));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.id, props.data, needRefreshData, dispatch, dateSendEmail]);

  useEffect(() => {
    dispatch(handleGetCustomerFiles({ id: props.id }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, props.id]);

  //#endregion Implement Hook
  // //#region Handle Function
  const handleCancel = () => {
    handleSetOpen(false);
  };
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      try {
        await dispatch(handleSendMail(values)).unwrap();
        notify('success', t('sendMailSuccess', { ns: 'admin/common' }));
        dispatch(customerActions.changeNeedRefreshData(true));
        handleSetOpen(false);
      } catch (e: unknown) {
        handleApiError(e, setFieldError);
      }
      return;
    },
  });
  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();

    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  //#endregion Handle Function

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {t('sendMail')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <BaseSendEmail
          onChange={(value) => {
            setDataSendEmail(value);
          }}
          formik={form}
          fileType='customer'
          customerId={props.id}
        />
      </DialogContent>

      <DialogActions className='justify-end'>
        <Button color='secondary' variant='contained' onClick={() => handleCancel()}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button
          color='primary'
          variant='contained'
          onClick={() => {
            form.handleSubmit();
          }}
        >
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(SendMailDialog);
